import React, { useState } from 'react';
import { DateTime, Info } from 'luxon';
import { Box, Button } from '@sendible/design-system';
import { Actions, Container, Grid, Header } from './index.styles';
import { DateButton } from './components';

type CalendarProps = {
  closeHandler: () => void;
  dateSelectedCallback: (date: CampaignsDateTime) => void;
  isStartOrEndDate: IsStartOrEndDateType;
  selectedDateRanges: SelectedDateRangeType;
  weekStart?: 0 | 1;
};

export const DatePicker = ({ dateSelectedCallback, isStartOrEndDate, selectedDateRanges, weekStart = 0 }: CalendarProps) => {
  const currentDate = DateTime.now().toUTC();
  const { startDate, endDate } = selectedDateRanges;

  const [visibleDate, setVisibleDate] = useState(() => {
    if (isStartOrEndDate === 'start' && startDate) return startDate.toUTC();
    if (isStartOrEndDate === 'end' && endDate) return endDate.toUTC();

    return currentDate;
  });

  const firstDayOfMonth = DateTime.fromObject({ year: visibleDate.year, month: visibleDate.month, day: 1 });

  const getWeekdayOffset = (weekday: number) => {
    return (weekday - weekStart + 7) % 7;
  };

  const emptyFieldsStart =
    firstDayOfMonth.weekday !== 7 || weekStart === 1 ? new Array(getWeekdayOffset(firstDayOfMonth.weekday)).fill(undefined) : [];

  const items = new Array(visibleDate.daysInMonth)
    .fill('')
    .map((_, indx) => DateTime.fromObject({ year: visibleDate.year, month: visibleDate.month, day: indx + 1 }));
  const calendarDates = [...emptyFieldsStart, ...items];

  while (calendarDates.length % 7 !== 0) {
    calendarDates.push(undefined);
  }

  const weekdayLabels = (() => {
    const weekdays = Info.weekdays('short');

    return weekStart === 0 ? [...weekdays.slice(6), ...weekdays.slice(0, 6)] : weekdays;
  })();

  return (
    <Container>
      <Header>
        <Actions>
          <Button
            onClick={() => setVisibleDate((vDate: DateTime) => vDate.minus({ months: 1 }).toUTC())}
            data-testid="icon-left"
            icon="chevron_left"
            size={14}
          />
          <Button
            onClick={() => setVisibleDate((vDate: DateTime) => vDate.plus({ months: 1 }).toUTC())}
            data-testid="icon-right"
            icon="chevron_right"
            size={14}
          />
        </Actions>
        <p data-testid="d-p-month">{visibleDate.monthLong}</p>
        <p data-testid="d-p-year">{visibleDate.year}</p>
      </Header>
      <Grid>
        {weekdayLabels.map((label, index) => (
          <div key={`weekday-${index}`}>
            <p data-testid="d-p-weekday">{label[0]}</p>
          </div>
        ))}
      </Grid>
      <Grid data-testid="d-t-calendarDates">
        {calendarDates.map((dayDate, dIndex) => (
          <DateButton
            date={dayDate}
            index={dIndex}
            selectedDateRanges={selectedDateRanges}
            dateSelectedCallback={dateSelectedCallback}
            key={`date-button-${dIndex}`}
          />
        ))}
      </Grid>
      <Box contentJustify="flex-end">
        <Button
          data-testid="d-p-clearButton"
          onClick={() => {
            dateSelectedCallback(null);
          }}
          label="Clear"
        />
      </Box>
    </Container>
  );
};
